import { fmtTime, useFocusableWhenScrollable, zonedParts } from '@pa/ui'
import { Building2, CalendarDays, Phone, Stethoscope, UserRound } from 'lucide-react'
import type { ReactNode } from 'react'
import type { Slot } from '../model/machine'
import type { Choice } from '../model/resolve'
import type { PublicOptions } from '@pa/api/public'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/** "Wed, Oct 14" for the summary (the mockup's short date), in clinic time. */
function shortDate(iso: string, tz: string): string {
  const [y = 0, m = 1, d = 1] = zonedParts(iso, tz).date.split('-').map(Number)
  return `${WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? ''}, ${MONTHS[m - 1] ?? ''} ${String(d)}`
}

/**
 * The layout shared by P-07 to P-10 (UserFlows §2.3), ported from the mockup's `patient/BookingFlow.tsx`: the summary
 * panel ("Hi {first name}, book your visit", the visit type or "New appointment", Location / Visit type / Provider /
 * Date & time with "Not selected yet", "Questions? Call {phone}") and the progress bar ("Step X of N").
 */
export function BookingLayout({
  firstName,
  options,
  choice,
  phone,
  step,
  steps,
  slot = null,
  timeZone = 'UTC',
  progress = true,
  children,
}: {
  firstName: string
  options: PublicOptions
  choice: Choice
  phone: string
  step: number
  steps: number
  /** The chosen or held time (Date & time row), in clinic time. */
  slot?: Slot | null
  timeZone?: string
  /** P-10 shows the summary without the progress bar (mockup). */
  progress?: boolean
  children: ReactNode
}) {
  const type = options.visitTypes.find((t) => t.id === choice.visitTypeId)
  const location = options.locations.find((l) => l.id === choice.locationId)
  const provider = options.providers.find((p) => p.id === choice.providerId)
  const tele = type?.telehealth === true
  const locationSub = location ? (tele ? 'Video visit from this office' : location.address) : undefined
  // In the widget the summary scrolls on its own when the content is long (long names, a long address).
  const summaryRef = useFocusableWhenScrollable<HTMLElement>()
  return (
    <div className="pt-shell">
      <aside className="pt-summary" aria-label="Your visit" ref={summaryRef}>
        <div>
          <div className="eyebrow">Hi {firstName}, book your visit</div>
          <h2>{type ? type.name : 'New appointment'}</h2>
        </div>
        <div className="sum-list">
          <SumItem icon={<Building2 />} label="Location" value={location?.name} sub={locationSub} subClass="addr" />
          <SumItem
            icon={<Stethoscope />}
            label="Visit type"
            value={type?.name}
            sub={tele ? 'Video visit' : undefined}
          />
          <SumItem
            icon={<UserRound />}
            label="Provider"
            value={provider ? [provider.name, provider.credentials].filter((v) => v !== '').join(', ') : undefined}
          />
          <SumItem
            icon={<CalendarDays />}
            label="Date & time"
            value={slot ? shortDate(slot.start, timeZone) : undefined}
            sub={slot ? fmtTime(slot.start, timeZone) : undefined}
          />
        </div>
        {phone !== '' && (
          <div className="pt-summary-foot">
            <span>
              <Phone aria-hidden /> Questions? Call {phone}
            </span>
          </div>
        )}
      </aside>
      <section className="pt-content">
        {progress && (
          <div className="pt-progress" role="img" aria-label={`Step ${String(step)} of ${String(steps)}`}>
            {Array.from({ length: steps }, (_, i) => (
              <i key={i} className={i < step ? 'done' : ''} />
            ))}
          </div>
        )}
        {children}
      </section>
    </div>
  )
}

function SumItem({
  icon,
  label,
  value,
  sub,
  subClass,
}: {
  icon: ReactNode
  label: string
  value: string | undefined
  sub?: string | undefined
  subClass?: string
}) {
  const filled = value !== undefined
  return (
    <div className={`sum-item ${filled ? 'filled' : 'pending'}`}>
      <span className="s-ic" aria-hidden>
        {icon}
      </span>
      <div>
        <span>{label}</span>
        <b>{value ?? 'Not selected yet'}</b>
        {filled && sub !== undefined && sub !== '' && (
          <span className={subClass} style={{ display: 'block' }}>
            {sub}
          </span>
        )}
      </div>
    </div>
  )
}
